import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  CircularProgress,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import { 
  Send as SendIcon, 
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import ConnectionStatus from './ConnectionStatus';
import api, { fileApi } from '../services/api';

interface MessageFile {
  id: number;
  filename: string;
  file_type: string;
  file_size: number;
  url: string;
  thumbnail_url?: string;
}

interface Message {
  id: string;
  content: string;
  created_at: string;
  task_id?: number;
  sender: {
    id: number;
    full_name: string;
    email: string;
  };
  files?: MessageFile[];
}

interface TaskChatProps {
  taskId: string;
  otherUserId?: number;
}

const TaskChat: React.FC<TaskChatProps> = ({ taskId, otherUserId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { socket, isConnected, lastError, reconnect } = useWebSocket();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canSend = isConnected && !uploadingFiles;

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        // При flexDirection: 'column-reverse', скролването е обърнато
        // scrollTop = 0 означава дъно (последни съобщения)
        messagesContainerRef.current.scrollTo({
          top: 0,
          behavior: behavior
        });
      }
    }, 100);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...filesArray]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (isInitialLoad) {
      fetchMessages();
      setIsInitialLoad(false);
    }
    
    if (!socket) return;

    const handleSocketMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        // Backend изпраща 'new_message' за всички нови съобщения
        if (data.type === 'new_message') {
          const message = data.payload as Message;

          // Проверяваме дали съобщението е за тази задача
          // ВАЖНО: taskId е string, а message.task_id е number - трябва да конвертираме!
          if (message.task_id === parseInt(taskId, 10)) {
            // Избягваме дубликати
            setMessages(prev => {
              const exists = prev.some(m => m.id === message.id);
              if (exists) {
                return prev;
              }
              return [...prev, message];
            });
            scrollToBottom();
          }
        }
      } catch (error) {
        console.error('TaskChat: Error parsing WebSocket message:', error);
      }
    };

    socket.addEventListener('message', handleSocketMessage);
    return () => socket.removeEventListener('message', handleSocketMessage);
  }, [taskId, socket, isInitialLoad]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    if (!otherUserId) {
      setError(t('chat.errors.fetch_failed'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(
        `/api/v1/chat-messages/with/${otherUserId}?task_id=${taskId}&skip=${(page - 1) * 20}&limit=20`
      );
      const data = response.data;
      
      if (data.length < 20) {
        setHasMore(false);
      }
      
      const sortedMessages = data.sort((a: Message, b: Message) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      
      // Филтрираме дубликати преди да добавим
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const newMessages = sortedMessages.filter((m: Message) => !existingIds.has(m.id));
        return [...prev, ...newMessages];
      });
      setPage(prev => prev + 1);
      
      // Скролваме до дъното след първоначално зареждане (с 'auto' за незабавно скролване)
      // Използваме по-дълъг timeout за да се увери че DOM-ът е напълно рендериран
      setTimeout(() => scrollToBottom('auto'), 300);
    } catch (err) {
      console.error('TaskChat: Error fetching messages:', err);
      setError(t('chat.errors.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim() && selectedFiles.length === 0) return;

    if (!isConnected) {
      setError(t('chat.errors.send_failed'));
      return;
    }

    try {
      // ПЪРВО качваме файловете (ако има)
      // ВАЖНО: НЕ подаваме task_id, защото файловете от чата трябва да са асоциирани само с message_id
      let uploadedFileIds: number[] = [];
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          // НЕ добавяме task_id - файлът ще бъде асоцииран с message_id от backend
          
          const uploadResponse = await api.post('/api/v1/files/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (!uploadResponse.data) {
            throw new Error('Failed to upload file');
          }
          
          const uploadedFile = uploadResponse.data;
          uploadedFileIds.push(uploadedFile.id);
        }
        
        // Изчистваме избраните файлове
        setSelectedFiles([]);
      }
      
      // СЛЕД ТОВА изпращаме съобщението с file_ids
      const response = await api.post('/api/v1/chat-messages', {
        task_id: taskId,
        recipient_id: otherUserId,
        content: newMessage.trim() || (uploadedFileIds.length > 0 ? '' : ''),
        file_ids: uploadedFileIds.length > 0 ? uploadedFileIds : undefined
      });

      const message = response.data;
      
      // Избягваме дубликати
      setMessages(prev => {
        const exists = prev.some(m => m.id === message.id);
        if (exists) return prev;
        return [...prev, message];
      });
      setNewMessage('');
      scrollToBottom();
    } catch (err) {
      console.error('TaskChat: Error sending message:', err);
      setError(t('chat.errors.send_failed'));
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('bg-BG', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' });
  };

  return (
    <Box data-testid="task-chat-container" sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('task.chat')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: isConnected ? 'success.main' : 'error.main',
                mr: 1
              }}
            />
            <Typography variant="body2" color={isConnected ? 'success.main' : 'error.main'}>
              {isConnected ? t('chat.connected') : t('chat.disconnected')}
            </Typography>
          </Box>
        </Box>

        <Box
          ref={messagesContainerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column-reverse',
          }}
        >
          <List>
            {messages.map((message, index) => {
              // Defensive check for sender
              if (!message.sender) {
                return null;
              }
              
              return (
                <ListItem
                  key={`${message.id}-${message.created_at}-${index}`}
                  sx={{
                    flexDirection: message.sender.id === user?.id ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>{message.sender.full_name?.[0] || '?'}</Avatar>
                  </ListItemAvatar>
                  <Box sx={{ maxWidth: '70%' }}>
                    <ListItemText
                      primary={message.content}
                      secondary={`${message.sender.full_name || 'Unknown'} • ${formatDate(message.created_at)}`}
                      sx={{
                        '& .MuiListItemText-primary': {
                          backgroundColor: message.sender.id === user?.id ? 'primary.main' : 'grey.200',
                          color: message.sender.id === user?.id ? 'white' : 'text.primary',
                          padding: 2,
                          borderRadius: 2,
                          display: 'inline-block',
                        },
                      }}
                    />
                    
                    {/* Показване на прикачените файлове */}
                    {message.files && message.files.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {message.files.map((file) => {
                          const isImage = file.file_type.startsWith('image/');
                          const canDelete = message.sender.id === user?.id;
                          
                          return (
                            <Box key={file.id} sx={{ position: 'relative' }}>
                              {/* Thumbnail за снимки */}
                              {isImage ? (
                                <>
                                  <Box
                                    component="a"
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      display: 'block',
                                      width: 150,
                                      height: 150,
                                      backgroundImage: `url(${file.thumbnail_url || file.url})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      borderRadius: 1,
                                      cursor: 'pointer',
                                      '&:hover': { opacity: 0.8 }
                                    }}
                                  />
                                  {canDelete && (
                                    <IconButton
                                      size="small"
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        if (window.confirm(t('chat.confirm_delete_file'))) {
                                          try {
                                            await fileApi.deleteFile(file.id);
                                            
                                            // Успешно изтрит файл - обновяваме съобщението без изтрития файл
                                            setMessages(prev => prev.map(m => 
                                              m.id === message.id 
                                                ? { ...m, files: m.files?.filter(f => f.id !== file.id) }
                                                : m
                                            ));
                                          } catch (error) {
                                            console.error('Error deleting file:', error);
                                          }
                                        }
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        color: 'white',
                                        '&:hover': {
                                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </>
                              ) : (
                                <Chip
                                  icon={<FileIcon />}
                                  label={file.filename}
                                  size="small"
                                  clickable
                                  component="a"
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    maxWidth: '200px',
                                    backgroundColor: message.sender.id === user?.id ? 'primary.light' : 'grey.300',
                                  }}
                                  onDelete={canDelete ? async (e) => {
                                    e.preventDefault();
                                    if (window.confirm(t('chat.confirm_delete_file'))) {
                                      try {
                                        await fileApi.deleteFile(file.id);
                                        
                                        // Успешно изтрит файл - обновяваме съобщението без изтрития файл
                                        setMessages(prev => prev.map(m => 
                                          m.id === message.id 
                                            ? { ...m, files: m.files?.filter(f => f.id !== file.id) }
                                            : m
                                        ));
                                      } catch (error) {
                                        console.error('Error deleting file:', error);
                                      }
                                    }
                                  } : undefined}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
          
          <ConnectionStatus isConnected={isConnected} lastError={lastError} reconnect={reconnect} />
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          )}
          
          {hasMore && !loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Button onClick={fetchMessages}>{t('chat.load_more')}</Button>
            </Box>
          )}
          
          {error && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={fetchMessages}>
                    {t('common.retry', 'Опитай отново')}
                  </Button>
                }
              >
                {error}
              </Alert>
            </Box>
          )}
          
          {messages.length === 0 && !loading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="textSecondary">
                {t('chat.no_messages')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('chat.start_conversation', 'Започни разговора с първото съобщение.')}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          {/* Показване на избраните файлове */}
          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {selectedFiles.map((file, index) => (
                  <Chip
                    key={index}
                    icon={file.type.startsWith('image/') ? <ImageIcon /> : <FileIcon />}
                    label={file.name}
                    onDelete={() => handleRemoveFile(index)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Форма за изпращане */}
          <Box
            component="form"
            onSubmit={handleSendMessage}
            sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
          >
            {/* Скрит file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            
            {/* Бутон за прикачване на файлове */}
            <IconButton
              color="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canSend}
              aria-label={t('chat.attach_file')}
              title={t('chat.attach_file')}
            >
              <AttachFileIcon />
            </IconButton>

            <TextField
              fullWidth
              inputProps={{ 'data-testid': 'task-chat-message-input' }}
              placeholder={t('chat.type_message')}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              variant="outlined"
              size="small"
              disabled={!canSend}
            />
            
            <IconButton
              color="primary"
              type="submit"
              disabled={(!newMessage.trim() && selectedFiles.length === 0) || !canSend}
              data-testid="task-chat-send-button"
              aria-label={t('chat.send')}
              title={t('chat.send')}
            >
              {uploadingFiles ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default TaskChat; 